import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { usePortalContext } from '@/layouts/CustomerPortalLayout';
import { useMarketData } from '@/hooks/useMarketData';
import { PHASES } from '@/hooks/usePortal';
import { formatPriceCt, formatCurrency, formatRelativeTime, formatDate } from '@/utils/formatters';
import {
  TrendingUp, TrendingDown, Minus, DollarSign, FileText, Clock,
  CheckCircle2, Phone, Mail, ArrowRight, Inbox, Send, Package, Star,
  Check, Shield, Calendar, Activity, Zap
} from 'lucide-react';

const iconMap = {
  inbox: Inbox, check: Check, send: Send, package: Package, star: Star,
  'check-circle': CheckCircle2, phone: Phone,
};

const PortalDashboard = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { data } = usePortalContext();
  const { marketData, isLoading: marketLoading, error: marketError } = useMarketData();

  const { lead, company, contact, offers, timeline, sales } = data;
  const currentPhaseIdx = PHASES.findIndex(p => p.id === lead.phase);
  const acceptedOffer = offers.find(o => o.status === 'angenommen');
  const activeOffer = offers.find(o => o.status === 'gesendet' || o.status === 'geoeffnet') || offers[0];

  // Calculated savings estimate
  const estimatedSavings = activeOffer?.options?.length
    ? Math.max(...activeOffer.options.map(o => o.savings_eur || 0))
    : (company.annual_consumption_kwh && company.current_price_ct_kwh)
      ? Math.round(company.annual_consumption_kwh * company.current_price_ct_kwh * 0.15 / 100)
      : 0;

  const daysActive = Math.max(1, Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 86400000));
  const marketOk = !marketError && typeof marketData?.basePrice === 'number';
  const currentEpex = marketOk ? formatPriceCt(marketData.basePrice * 100) : '–';
  const trend = marketOk ? marketData?.trend : undefined;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  const greeting = contact?.last_name ? `Herr ${contact.last_name}` : contact?.first_name || '';

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Willkommen{greeting ? `, ${greeting}` : ''}</h1>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
          <Calendar size={14} />
          Wir sind seit <strong className="text-foreground">{daysActive} {daysActive === 1 ? 'Tag' : 'Tagen'}</strong> fuer <strong className="text-foreground">{company.name}</strong> aktiv
        </p>
      </div>

      {/* Progress Bar */}
      <div className="hk-card">
        <div className="hk-card-body">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-foreground">Ihr Prozess-Fortschritt</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Wir halten Sie jederzeit auf dem Laufenden</p>
            </div>
            <span className="hk-badge primary">Schritt {currentPhaseIdx + 1} von {PHASES.length}</span>
          </div>

          <div className="relative">
            {/* Progress line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
            <div
              className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500"
              style={{ width: `${(currentPhaseIdx / (PHASES.length - 1)) * 100}%` }}
            />

            {/* Steps */}
            <div className="relative grid grid-cols-7 gap-1">
              {PHASES.map((phase, idx) => {
                const isDone = idx < currentPhaseIdx;
                const isCurrent = idx === currentPhaseIdx;
                return (
                  <div key={phase.id} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-bold text-xs transition-all ${
                      isDone
                        ? 'bg-primary border-primary text-primary-foreground'
                        : isCurrent
                        ? 'bg-card border-primary text-primary ring-4 ring-primary/20'
                        : 'bg-card border-border text-muted-foreground'
                    }`}>
                      {isDone ? <Check size={14} /> : idx + 1}
                    </div>
                    <div className={`mt-2 text-center text-[10px] font-semibold ${isCurrent ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {phase.label}
                    </div>
                    <div className="text-[9px] text-muted-foreground text-center hidden sm:block">{phase.description}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">EPEX Spotpreis</p>
              <p className="stat-value">{marketLoading ? '...' : `${currentEpex} ct`}</p>
              <div className="flex items-center gap-1 mt-1 text-xs">
                {marketOk && (
                  <TrendIcon size={12} className={trend === 'up' ? 'text-destructive' : trend === 'down' ? 'text-success' : 'text-muted-foreground'} />
                )}
                <span className="text-muted-foreground">
                  {marketOk ? 'Live-Marktpreis' : 'Derzeit nicht abrufbar'}
                </span>
              </div>
            </div>
            <div className="stat-icon bg-accent text-primary"><Zap size={20} /></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Ihre Ersparnis</p>
              <p className="stat-value" style={{ color: 'var(--color-success)' }}>{formatCurrency(estimatedSavings).replace(',00', '')}</p>
              <p className="text-xs text-muted-foreground mt-1">pro Jahr (geschaetzt)</p>
            </div>
            <div className="stat-icon" style={{ background: '#e6fbf2', color: '#00a864' }}><TrendingUp size={20} /></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Aktive Angebote</p>
              <p className="stat-value">{activeOffer?.options?.length || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">von Versorgern im Vergleich</p>
            </div>
            <div className="stat-icon" style={{ background: '#f0e6ff', color: '#7429F8' }}><FileText size={20} /></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">Naechster Schritt</p>
              <p className="text-lg font-bold text-foreground mt-1.5 leading-tight">{lead.next_action || 'Warten auf Angebot'}</p>
              {lead.next_action_date && (
                <p className="text-xs text-muted-foreground mt-1">bis {formatDate(lead.next_action_date)}</p>
              )}
            </div>
            <div className="stat-icon" style={{ background: '#fff8e0', color: '#b8860b' }}><Clock size={20} /></div>
          </div>
        </div>
      </div>

      {/* Main grid: Timeline + Contact */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2">
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Ihre Prozess-Historie</h6>
              <Link to={`/portal/${token}/dokumente`} className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                Alle Ereignisse <ArrowRight size={12} />
              </Link>
            </div>
            <div className="hk-card-body">
              {timeline.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="mx-auto text-muted-foreground mb-2" size={32} />
                  <p className="text-sm text-muted-foreground">Noch keine Ereignisse</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-border" />
                  <div className="space-y-5">
                    {timeline.slice(0, 6).map((event) => {
                      const Icon = iconMap[event.icon] || Activity;
                      return (
                        <div key={event.id} className="relative flex items-start gap-4">
                          <div className="relative z-10 w-10 h-10 rounded-full bg-accent border-2 border-card flex items-center justify-center flex-shrink-0">
                            <Icon size={16} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0 pt-1">
                            <p className="text-sm font-semibold text-foreground">{event.title}</p>
                            {event.description && <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>}
                            <p className="text-[11px] text-muted-foreground mt-1">{formatRelativeTime(event.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sales Contact */}
        <div>
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Ihr Ansprechpartner</h6>
            </div>
            <div className="hk-card-body text-center">
              <div className="hk-avatar mx-auto mb-3" style={{ background: 'var(--primary)', width: '4rem', height: '4rem', fontSize: '1.25rem' }}>
                {sales.full_name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="font-bold text-foreground">{sales.full_name}</div>
              <div className="text-xs text-muted-foreground mb-4">{sales.role}</div>

              <div className="space-y-2">
                <a href={`tel:${sales.phone}`} className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">
                  <Phone size={14} />
                  {sales.phone}
                </a>
                <a href={`mailto:${sales.email}`} className="flex items-center justify-center gap-2 w-full border border-border text-foreground py-2.5 rounded-lg text-sm font-semibold hover:bg-muted transition-colors">
                  <Mail size={14} />
                  E-Mail senden
                </a>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Shield size={12} className="text-success" />
                  <span>Antwort innerhalb von 2 Stunden</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick stat */}
          <div className="hk-card mt-5" style={{ background: 'linear-gradient(135deg, #007D88 0%, #18DDEF 100%)', color: '#fff', borderColor: 'transparent' }}>
            <div className="hk-card-body text-center">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">Vertrauen schafft Sicherheit</div>
              <div className="text-3xl font-bold mt-1">500+</div>
              <div className="text-xs opacity-90">Unternehmen nutzen AIQCON bereits</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      {activeOffer && !acceptedOffer && (
        <div className="hk-card" style={{ borderColor: 'var(--primary)', borderWidth: '2px' }}>
          <div className="hk-card-body flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="text-primary" size={24} />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">Ihr Angebot ist bereit</h3>
                <p className="text-sm text-muted-foreground">Wir haben {activeOffer.options?.length || 3} Optionen fuer Sie zusammengestellt. Vergleichen und waehlen Sie in wenigen Klicks.</p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/portal/${token}/angebote`)}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
            >
              Angebote ansehen
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalDashboard;
